/**
 * Shared motion tokens for use with motion/react (framer-motion).
 *
 * Keep values in sync with --ease-* and --duration-* tokens in
 * packages/lattice/tailwind.css. Source: https://animations.dev
 * (Emil Kowalski, "Animations on the Web").
 *
 * Usage:
 *   import { EASING, DURATION } from "@replohq/lattice/utils/motion";
 *
 *   <motion.div
 *     transition={{ duration: DURATION.standard, ease: EASING.outQuint }}
 *   />
 */

/**
 * Easing curves. Use ease-out for enter/exit, ease-in-out for on-screen
 * movement, ease for hover transitions.
 *
 * The `as const` assertion is required so each value is typed as a 4-tuple
 * rather than `number[]`, which is what motion/react's `transition.ease`
 * prop expects.
 */
export const EASING = {
  outQuad: [0.25, 0.46, 0.45, 0.94],
  outCubic: [0.215, 0.61, 0.355, 1],
  outQuart: [0.165, 0.84, 0.44, 1],
  outQuint: [0.23, 1, 0.32, 1],
  outExpo: [0.19, 1, 0.22, 1],
  inOutQuart: [0.77, 0, 0.175, 1],
} as const;

/**
 * Duration in seconds (matches motion/react's `transition.duration`).
 *
 * - `micro` — micro-interactions (100ms)
 * - `fast` — small UI changes (150ms)
 * - `standard` — standard UI, view swaps (200ms)
 * - `slow` — large overlays and panels (300ms)
 */
export const DURATION = {
  micro: 0.1,
  fast: 0.15,
  standard: 0.2,
  slow: 0.3,
} as const;
